import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Edit2, Copy, X, AlertTriangle, Play } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'
import ZoneEditor from './ZoneEditor'
import {
  fetchAndParseVast,
  fireBeacon,
  servingImgUrl,
  servingClickUrl,
  servingHtmlUrl,
} from '../shared/adspeedClient'
import LinkAdsToEntityModal from '../shared/LinkAdsToEntityModal'
import '../SponsorManager.css'

const ADTAG_FORMATS = [
  { value: 'vast',          label: 'VAST URL' },
  { value: 'javascript',    label: 'JavaScript' },
  { value: 'iframe',        label: 'IFrame' },
  { value: 'comprehensive', label: 'Comprehensive' },
  { value: 'simplified',    label: 'Simplified' },
]

export default function ZoneDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { api } = useAuth()

  const [zone, setZone] = useState(null)
  const [tbtvTags, setTbtvTags] = useState([])
  const [allTags, setAllTags] = useState([])
  const [stale, setStale] = useState(false)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [status, setStatus] = useState('')

  // Tag input state
  const [tagInput, setTagInput] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const tagInputRef = useRef(null)

  // Ad-tag embed state
  const [adtagFormat, setAdtagFormat] = useState('javascript')
  const [adtag, setAdtag] = useState(null)
  const [adtagLoading, setAdtagLoading] = useState(false)

  // Live-fire state (parallel to AdDetail's)
  const [liveFireMode, setLiveFireMode] = useState(null) // 'video' | 'image' | 'html'
  const [vastFire, setVastFire] = useState(null)
  const [vastFireLoading, setVastFireLoading] = useState(false)
  const [vastImpressionFired, setVastImpressionFired] = useState(false)
  const liveFireKey = useRef(0)

  // "Link ads to this zone" modal
  const [showLinkAds, setShowLinkAds] = useState(false)

  const loadZone = useCallback(async () => {
    try {
      const data = await api(`/sponsors/zones/${id}`)
      setZone(data.zone)
      setTbtvTags((data.tbtvTags || []).map(t => t.name))
      setStale(!!data.stale)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }, [api, id])

  const loadAllTags = useCallback(async () => {
    try {
      const data = await api('/tags')
      setAllTags(data.tags || [])
    } catch {}
  }, [api])

  useEffect(() => {
    setLoading(true)
    Promise.all([loadZone(), loadAllTags()]).finally(() => setLoading(false))
  }, [loadZone, loadAllTags])

  useEffect(() => {
    if (!id) return
    setAdtagLoading(true)
    api(`/sponsors/zones/${id}/adtag?format=${adtagFormat}`)
      .then(d => {
        if (d.ok === false) {
          setAdtag(null)
          setStatus(`Ad-tag error: ${d.error?.message || 'AdSpeed rejected the request'}`)
        } else {
          setAdtag(d.adtag)
        }
      })
      .catch(e => setStatus(`Ad-tag error: ${e.message}`))
      .finally(() => setAdtagLoading(false))
  }, [api, id, adtagFormat])

  async function handleEditSubmit(body) {
    const res = await api(`/sponsors/zones/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
    if (res?.ok === false) {
      throw new Error(res.error?.message || 'AdSpeed rejected the edit')
    }
    setEditing(false)
    setStatus('Zone updated')
    await loadZone()
  }

  async function persistTbtvTags(next) {
    try {
      await api(`/sponsors/zones/${id}/tbtv-tags`, {
        method: 'PUT',
        body: JSON.stringify({ tags: next }),
      })
      setStatus('Tags saved')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      await loadZone()
    }
  }

  function addTag(name) {
    const clean = name.trim().toLowerCase()
    if (!clean || tbtvTags.includes(clean)) return
    const next = [...tbtvTags, clean]
    setTbtvTags(next)
    setTagInput('')
    setShowSuggestions(false)
    persistTbtvTags(next)
  }

  function removeTag(name) {
    const next = tbtvTags.filter(t => t !== name)
    setTbtvTags(next)
    persistTbtvTags(next)
  }

  function handleTagKey(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (tagInput.trim()) addTag(tagInput)
    } else if (e.key === 'Backspace' && !tagInput && tbtvTags.length) {
      removeTag(tbtvTags[tbtvTags.length - 1])
    }
  }

  const filteredSuggestions = allTags
    .filter(t => t.toLowerCase().includes(tagInput.toLowerCase()) && !tbtvTags.includes(t))
    .slice(0, 8)

  async function startZoneVideoFire() {
    setVastFireLoading(true)
    setVastImpressionFired(false)
    const r = await fetchAndParseVast({ zid: id })
    if (r.ok) {
      setVastFire(r)
      liveFireKey.current += 1
      setLiveFireMode('video')
      setVastFireLoading(false)
      return
    }

    // Zone request was filtered by AdSpeed's selection logic (restrictions,
    // geo, website, freq cap, etc.). Fall back to playing the highest-weight
    // active linked ad directly via aid= — bypasses restrictions, still shows
    // what the user wants to see (one of the ads in this zone's rotation).
    const candidates = linkedAds.filter(a => (a.status ?? a.Status) === 'active')
    const pick = candidates.sort(
      (a, b) => Number(b.weight ?? b.Weight ?? 0) - Number(a.weight ?? a.Weight ?? 0)
    )[0]
    if (pick) {
      const pickId = pick.id ?? pick.AdId
      const pickName = pick.name ?? pick.Name
      const adRes = await fetchAndParseVast({ aid: pickId })
      if (adRes.ok) {
        setVastFire(adRes)
        liveFireKey.current += 1
        setLiveFireMode('video')
        setVastFireLoading(false)
        setStatus(`Zone request was filtered (${r.error}). Showing highest-weighted linked ad "${pickName}" via aid= bypass.`)
        return
      }
    }

    setStatus(`VAST player error: ${r.error}. No fallback ad available.`)
    setVastFireLoading(false)
  }

  function fireImpressionBeacon(url) {
    if (vastImpressionFired) return
    setVastImpressionFired(true)
    fireBeacon(url)
  }

  function openClickThrough(url) {
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  async function copyAdtag() {
    // Reuse the same string the textarea shows — handles ServingCode for
    // synthesized VAST URLs, Zone.ServingCode for AdSpeed-rendered tags, etc.
    if (!adtagText) return
    try {
      await navigator.clipboard.writeText(adtagText)
      setStatus('Ad-tag copied to clipboard')
    } catch {
      setStatus('Copy failed — please select manually')
    }
  }

  if (loading) {
    return (
      <div className="sponsors-page">
        <div className="muted">Loading zone…</div>
      </div>
    )
  }

  if (!zone) {
    return (
      <div className="sponsors-page">
        <button className="sponsors-refresh-btn" onClick={() => navigate('/sponsors/zones')}>
          <ArrowLeft size={14} /> Back to zones
        </button>
        <div className="sponsors-status error" style={{ marginTop: 12 }}>
          Zone {id} not found.
        </div>
      </div>
    )
  }

  const fields = [
    ['id', zone.id ?? zone.ZoneId ?? id],
    ['name', zone.name ?? zone.Name],
    ['description', zone.Description ?? zone.description],
    ['size', zone.size ?? zone.Size],
    ['type', zone.type ?? zone.Type],
    ['status', zone.status ?? zone.Status],
    ['rate', zone.rate ?? zone.Rate],
    ['created', zone.created ?? zone.Created],
  ]

  // Linked ads come from the AS.Zone.getInfo response: zone.Ads.Ad (array).
  const linkedAds = (() => {
    const raw = zone?.Ads?.Ad
    if (!raw) return []
    return Array.isArray(raw) ? raw : [raw]
  })()

  // Infer zone type from its linked ads' dimensions — zones don't have an
  // explicit type field in AS.Zone.getInfo's response.
  const sampleAd = linkedAds[0] || {}
  const sampleW = Number(sampleAd.width ?? sampleAd.Width ?? 0)
  const sampleH = Number(sampleAd.height ?? sampleAd.Height ?? 0)
  const zoneLooksVideo = sampleW >= 640 && sampleH >= 360 && (sampleW / sampleH).toFixed(2) >= 1.30
  const zoneLooksBanner = !zoneLooksVideo && sampleW > 0
  const fireW = sampleW || 300
  const fireH = sampleH || 250

  // AdSpeed shape: { Zone: { ServingCode: '...' } } for regular formats;
  // synthesized VAST shape: { ServingCode: '<url>', format: 'vast', type: 'url' }
  const adtagText = (() => {
    if (!adtag) return ''
    if (typeof adtag === 'string') return adtag
    if (typeof adtag?.ServingCode === 'string') return adtag.ServingCode
    if (typeof adtag?.Zone?.ServingCode === 'string') return adtag.Zone.ServingCode
    // Some shapes nest ServingCode as { '#text': '...' } from fast-xml-parser
    if (adtag?.Zone?.ServingCode?.['#text']) return adtag.Zone.ServingCode['#text']
    return adtag.AdTag || adtag.Tag || JSON.stringify(adtag, null, 2)
  })()

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <button className="sponsors-refresh-btn" onClick={() => navigate('/sponsors/zones')}>
            <ArrowLeft size={14} /> Zones
          </button>
          <h1>{zone.name ?? zone.Name ?? `Zone ${id}`}</h1>
          {stale && (
            <span className="sponsors-stale-badge">
              <AlertTriangle size={11} /> Stale cache
            </span>
          )}
        </div>
        <div className="sponsors-header-right">
          <button className="sponsors-refresh-btn" onClick={loadZone}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setEditing(true)}>
            <Edit2 size={14} /> Edit
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.toLowerCase().includes('error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="zone-detail-grid">
        <div>
          <div className="zone-detail-card">
            <h3>Zone properties</h3>
            {fields.map(([k, v]) => (
              <div className="zone-detail-field" key={k}>
                <span className="k">{k}</span>
                <span className="v">{v == null ? '—' : String(v)}</span>
              </div>
            ))}
          </div>

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Linked ads ({linkedAds.length})</h3>
              <button
                className="sponsors-refresh-btn"
                onClick={() => setShowLinkAds(true)}
                style={{ fontSize: '0.85rem' }}
              >
                + Link ads
              </button>
            </div>
            {linkedAds.length === 0 ? (
              <div className="muted" style={{ fontSize: '0.85rem' }}>No ads linked to this zone.</div>
            ) : (
              <table className="zones-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Weight</th>
                    <th>Size</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedAds.map(a => (
                    <LinkedAdRow
                      key={a.id ?? a.AdId}
                      ad={a}
                      api={api}
                      zoneId={id}
                      zoneName={zone?.name ?? zone?.Name ?? ''}
                      onSaved={msg => { setStatus(msg); loadZone() }}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <h3>Ad-tag embed</h3>
            <div className="adtag-format-row">
              {ADTAG_FORMATS.map(f => (
                <button
                  key={f.value}
                  className={`adtag-format-btn ${adtagFormat === f.value ? 'active' : ''}`}
                  onClick={() => setAdtagFormat(f.value)}
                >
                  {f.label}
                </button>
              ))}
              <button
                className="sponsors-refresh-btn"
                onClick={copyAdtag}
                disabled={!adtagText}
                style={{ marginLeft: 'auto' }}
              >
                <Copy size={13} /> Copy
              </button>
            </div>
            <div className="adtag-viewer">
              {adtagLoading ? (
                <div className="muted">Loading ad-tag…</div>
              ) : (
                <textarea readOnly value={adtagText} />
              )}
            </div>
          </div>

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <h3>Live ad-fire preview</h3>
            <div className="muted" style={{ fontSize: '0.85rem', marginBottom: 10 }}>
              Renders whatever ad AdSpeed currently selects from this zone's
              rotation. Loading the creative fires <strong>one impression</strong>;
              clicking opens the destination URL (and registers a click).
              Inferred type: <strong>{zoneLooksVideo ? 'video' : zoneLooksBanner ? 'banner' : 'unknown'}</strong>
              {sampleW > 0 && ` · using ${fireW}×${fireH}`}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {zoneLooksVideo && (
                <button
                  className="sponsors-refresh-btn"
                  onClick={startZoneVideoFire}
                  disabled={vastFireLoading}
                >
                  <Play size={13} /> {vastFireLoading ? 'Loading VAST…' : 'Fire video (VAST)'}
                </button>
              )}
              {zoneLooksBanner && (
                <>
                  <button
                    className="sponsors-refresh-btn"
                    onClick={() => { liveFireKey.current += 1; setLiveFireMode('image') }}
                  >
                    <Play size={13} /> Fire IMG
                  </button>
                  <button
                    className="sponsors-refresh-btn"
                    onClick={() => { liveFireKey.current += 1; setLiveFireMode('html') }}
                  >
                    <Play size={13} /> Fire HTML
                  </button>
                </>
              )}
              {!zoneLooksVideo && !zoneLooksBanner && (
                <span className="muted" style={{ fontSize: '0.78rem' }}>
                  Link an ad to this zone to enable preview.
                </span>
              )}
              {liveFireMode && (
                <button
                  className="sponsors-refresh-btn"
                  onClick={() => setLiveFireMode(null)}
                  style={{ marginLeft: 'auto' }}
                >
                  <X size={13} /> Stop
                </button>
              )}
            </div>

            {liveFireMode === 'video' && vastFire?.mediafile && (
              <div key={liveFireKey.current}>
                <video
                  controls
                  autoPlay
                  src={vastFire.mediafile}
                  onPlay={() => fireImpressionBeacon(vastFire.impression)}
                  onClick={() => openClickThrough(vastFire.clickThrough)}
                  style={{
                    width: '100%', maxHeight: 360, background: '#000',
                    borderRadius: 6, cursor: vastFire.clickThrough ? 'pointer' : 'default',
                  }}
                />
                <div className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>
                  {vastImpressionFired ? '✓ Impression fired' : 'Press play to fire impression'}
                  {vastFire.duration && ` · Duration ${vastFire.duration}`}
                  {vastFire.servedAdId && ` · Served ad ${vastFire.servedAdId}`}
                  {vastFire.clickThrough && ' · Click video to open destination URL'}
                </div>
              </div>
            )}

            {liveFireMode === 'image' && (
              <div key={liveFireKey.current}>
                <a
                  href={servingClickUrl({ zid: id, width: fireW, height: fireH })}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Click to open destination URL (fires click tracking)"
                >
                  <img
                    src={servingImgUrl({ zid: id, width: fireW, height: fireH })}
                    alt="zone ad"
                    style={{ maxWidth: '100%', maxHeight: 360, borderRadius: 6, background: '#000', cursor: 'pointer' }}
                  />
                </a>
                <div className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>
                  ✓ Impression fired on image load. Click image to fire click tracking.
                </div>
              </div>
            )}

            {liveFireMode === 'html' && (
              <div key={liveFireKey.current}>
                <iframe
                  title="Zone HTML fire"
                  src={servingHtmlUrl({ zid: id, width: fireW, height: fireH })}
                  width={fireW}
                  height={fireH}
                  style={{ border: '1px solid #333', background: '#000', maxWidth: '100%' }}
                  scrolling="no"
                />
                <div className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>
                  ✓ Impression fired on iframe load. (Iframe may render blank if
                  AdSpeed enforces X-Frame-Options — use Fire IMG instead.)
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="zone-detail-card">
            <h3>TBTV tags</h3>
            <div style={{ marginBottom: 10 }}>
              {tbtvTags.length === 0 && (
                <span className="muted" style={{ fontSize: '0.85rem' }}>No tags applied</span>
              )}
              {tbtvTags.map(t => (
                <span className="tag-chip" key={t}>
                  {t}
                  <button onClick={() => removeTag(t)} aria-label={`Remove ${t}`}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
            <div className="tag-input-wrap">
              <input
                ref={tagInputRef}
                className="tag-input"
                placeholder="Add tag…"
                value={tagInput}
                onChange={e => { setTagInput(e.target.value); setShowSuggestions(true) }}
                onKeyDown={handleTagKey}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              />
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="tag-suggestions">
                  {filteredSuggestions.map(s => (
                    <div
                      key={s}
                      className="tag-suggestion"
                      onMouseDown={() => addTag(s)}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="muted" style={{ fontSize: '0.78rem', marginTop: 8 }}>
              TBTV tags are stored locally and join to vader's tag system so
              playlists/content can cross-reference this zone.
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <ZoneEditor
          initial={zone}
          onClose={() => setEditing(false)}
          onSubmit={handleEditSubmit}
        />
      )}

      {showLinkAds && (
        <LinkAdsToEntityModal
          kind="zone"
          entityId={id}
          entityName={zone.name ?? zone.Name ?? `Zone ${id}`}
          alreadyLinkedAdIds={new Set(linkedAds.map(a => String(a.id ?? a.AdId)))}
          onClose={() => setShowLinkAds(false)}
          onComplete={(ok, fail, lastErr) => {
            setShowLinkAds(false)
            setStatus(fail === 0
              ? `Linked ${ok} ad(s) to this zone.`
              : `Linked ${ok}, ${fail} failed. ${lastErr}`)
            loadZone()
          }}
        />
      )}
    </div>
  )
}

// Linked-ad row with inline weight editor + click-to-navigate to AdDetail.
// Saves via PATCH /sponsors/ads/:id (AS.Ad.edit) for non-zero weights.
// A weight of 0 is treated as "remove from zone" — confirms and calls
// DELETE /sponsors/ads/:id/link (AS.Ad.unlinkFromZone) instead of
// editing the ad's weight to 0 (which would leave it linked but never
// served). The Zone Detail page reloads on success.
function LinkedAdRow({ ad, api, zoneId, zoneName, onSaved }) {
  const navigate = useNavigate()
  const adId = ad.id ?? ad.AdId
  const adName = ad.name ?? ad.Name ?? ''
  const initialWeight = ad.weight ?? ad.Weight ?? 1
  const [weight, setWeight] = useState(initialWeight)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const dirty = String(weight) !== String(initialWeight)

  async function saveWeight() {
    if (!dirty || saving) return
    const next = Number(weight)
    setSaving(true)
    setErr('')
    try {
      if (next === 0) {
        // Weight 0 → unlink from this zone. Confirm because it removes
        // the linkage entirely (re-linking later requires the bulk-link
        // modal).
        const ok = window.confirm(
          `Remove "${adName || `ad ${adId}`}" from this zone?\n\n` +
          `Setting weight to 0 will unlink the ad from this zone via ` +
          `AS.Ad.unlinkFromZone. The ad itself is not deleted.`
        )
        if (!ok) {
          setSaving(false)
          setWeight(initialWeight)
          return
        }
        const res = await api(`/sponsors/ads/${adId}/link`, {
          method: 'DELETE',
          body: JSON.stringify({
            kind: 'zone',
            target_id: zoneId,
            target_name: zoneName,
          }),
        })
        if (res?.ok === false) throw new Error(res.error?.message || 'AdSpeed rejected the unlink')
        onSaved(`Removed ad ${adId} from this zone`)
      } else {
        const res = await api(`/sponsors/ads/${adId}`, {
          method: 'PATCH',
          body: JSON.stringify({ weight: next }),
        })
        if (res?.ok === false) throw new Error(res.error?.message || 'AdSpeed rejected the edit')
        onSaved(`Weight saved for ad ${adId}`)
      }
    } catch (e) {
      setErr(e.message)
      setWeight(initialWeight) // revert
    }
    setSaving(false)
  }

  return (
    <tr
      onClick={() => navigate(`/sponsors/ads/${adId}`)}
      style={{ cursor: 'pointer' }}
    >
      <td>{adId}</td>
      <td>{ad.name ?? ad.Name}</td>
      <td>{ad.status ?? ad.Status}</td>
      <td onClick={e => e.stopPropagation()}>
        <input
          type="number"
          min="0"
          max="99"
          value={weight}
          onChange={e => setWeight(e.target.value)}
          onBlur={saveWeight}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur() } }}
          onClick={e => e.stopPropagation()}
          disabled={saving}
          style={{
            width: 60,
            padding: '3px 6px',
            background: dirty ? '#3a2a0a' : '#1a1a1a',
            border: '1px solid ' + (dirty ? '#f59e0b' : '#333'),
            borderRadius: 4,
            color: '#eee',
          }}
        />
        {err && <div style={{ color: '#f87171', fontSize: '0.72rem' }}>{err}</div>}
      </td>
      <td>{ad.width && ad.height ? `${ad.width}x${ad.height}` : ''}</td>
    </tr>
  )
}
